
import { createApp as prodCreateApp } from 'vue/dist/vue.esm-bundler.js';
import { createApp as devCreateApp, h } from 'vue';
import { createPinia } from 'pinia';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createInertiaApp } from '@inertiajs/vue3';
import type { App as IApp, Component, DefineComponent } from 'vue';
import { autoloadVueComponents } from 'virtual:vue-component-autoload';
import _ from 'lodash';
import axios from 'axios';
import * as dateFns from 'date-fns';
import moment from 'moment';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import { Switch } from '@headlessui/vue';
import Datepicker from 'vue3-datepicker';
import draggable from 'vuedraggable';
import SwapIcon from '@apparatix/icons/SwapIcon.vue';
import SearchInput from 'vue-search-input';
import 'vue-search-input/dist/styles.css';
import { ResolvablePromise } from '@apparatix/utilities/ResolvablePromise';
import { ZiggyVue } from 'ziggy-js';
import { Apparatix } from './theme/Apparatix';
import './theme/colors.css';
import { router } from './routes/router';
import { configureEcho } from '@laravel/echo-vue';
import { Elevation } from './constants/Elevation';
import { useSessionStore } from './stores/useSessionStore';
import { useBuildStore } from './stores/useBuildStore';
import { useBuildVersionCheck } from './composables/useBuildVersionCheck';
import { contractHeader, CONTRACT_REJECTED_STATUS, CONTRACT_REJECTION_HEADER } from './constants/ClientContract';

type AppCallback = (app: IApp) => void;
export interface IInit {
  beforeMount(callback: AppCallback): IBeforeMount;
  mount(): IMount;
}

interface IBeforeMount {
  mount(): IMount;
}

interface IMount {
  afterMount(callback: AppCallback): void;
}

export class AppFactory implements IInit, IBeforeMount, IMount {
  #app: IApp;
  #rootElement: Element | string;
  #appReady = new ResolvablePromise<boolean>();
  #internalSetupComplete = new ResolvablePromise<boolean>();
  #appMounted = new ResolvablePromise<boolean>;
  #readyToMount = Promise.all([this.#appReady.promise, this.#internalSetupComplete.promise]);
  #afterMountComplete = new ResolvablePromise<boolean>();

  private constructor(supportInertia: boolean = true, rootComponent?: Component) {
    const createApp = import.meta.env.DEV ? devCreateApp : prodCreateApp;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _self = this;

    if (supportInertia) {
      createInertiaApp({
        title: title => _self.#buildTitle(title),
        resolve: (name) => {
          if (name) {
            return resolvePageComponent<DefineComponent>(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue') as any);
          }
          // This is just inertia trying to do it's own thing with route changes, we just reject the promise,
          // because otherwise it would try and render a component on browser back/forward events, but we want vue-router to handle this
          return new Promise((__, reject) => reject(import.meta.env.DEV ? 'Ignore this error' : ''));
        },
        setup({
          el, App, props, plugin,
        }) {
          _self.#app = createApp({ render: () => h(App, props) }).use(plugin);
          _self.#rootElement = el;
        },
      }).then(() => _self.#appReady.resolve(true));
    } else {
      this.#app = createApp(rootComponent ?? {});
      this.#rootElement = '#app';
      this.#appReady.resolve(true);
    }
  }

  public static init(supportInertia: boolean = true, rootComponent?: Component): IInit {
    const instance = new AppFactory(supportInertia, rootComponent);
    instance.#appReady.then(() => instance.#internalBeforeMount());
    return instance;
  }

  public beforeMount(callback: (app: IApp) => void): IBeforeMount {
    if (typeof callback !== 'function') return this;
    this.#internalSetupComplete.then(() => callback(this.#app));
    return this;
  }

  public mount(): IMount {
    this.#readyToMount.then(() => {
      this.#app.mount(this.#rootElement);
      this.#appMounted.resolve(true);
    });

    return this;
  }

  public afterMount(callback: AppCallback): void {
    if (typeof callback !== 'function') return;
    this.#appMounted.then(() => {
      callback(this.#app);
      this.#afterMountComplete.resolve(true);
    });
  }

  /**
   * @description This is intended for use only in unit tests
   * Attempting to get an app instance from this method in live code will result in errors and/or unexpected behavior
   */
  public async getAppInstance() {
    await this.#readyToMount;
    return this.#app;
  }

  /**
   * @description This is intended for use only in unit tests
   */
  public async awaitSpecificStep(step: 'init' | 'before-mount' | 'mount' | 'after-mount'): Promise<boolean> {
    switch (step) {
      case 'init':
        return this.#appReady.promise;
      case 'before-mount':
        // eslint-disable-next-line no-case-declarations
        const all = await this.#readyToMount;
        return all.every(result => result === true);
      case 'mount':
        return this.#appMounted.promise;
      case 'after-mount':
        return this.#afterMountComplete.promise;
      default:
        return this.#afterMountComplete.promise;
    }
  }

  #internalBeforeMount() {
    const pinia = createPinia();

    this.#app.use(PrimeVue, {
      license: import.meta.env.VITE_PRIMEVUE_LICENSE_KEY,
      locale: { firstDayOfWeek: 1 },
      theme: {
        preset: Apparatix,
        options: {
          cssLayer: {
            name: 'primevue',
            order: 'tailwind-base, tailwind-utilities, primevue',
          },
          darkModeSelector: '.dark-mode-class-does-not-exist',
        },
      },
      zIndex: {
        modal: Elevation.modal,
        overlay: Elevation.overlay,
        menu: Elevation.menu,
        tooltip: Elevation.tooltip,
      },
    })
      .use(ConfirmationService)
      .use(DialogService)
      .use(ToastService)
      .use(ZiggyVue)
      .use(router)
      .use(pinia);

    this.#app.component('Datepicker', Datepicker);
    this.#app.component('Draggable', draggable);
    this.#app.component('SearchInput', SearchInput);

    this.#app.component('Switch', Switch);
    this.#app.component('SwapIcon', SwapIcon);

    autoloadVueComponents(this.#app);

    this.#app.directive('tooltip', Tooltip);

    /** @deprecated import lodash directly where needed */
    window._ = _;
    /** @deprecated import axios directly where needed */
    window.axios = axios;
    window.activeLoading = (action: 'start' | 'stop') => {
      if (action === 'start') {
        window.dispatchEvent(new Event('legacy-active-loading-start'));
      } else {
        window.dispatchEvent(new Event('legacy-active-loading-stop'));
      }
    };
    window._gridGlobalLightNavigationEnabled = false;
    window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

    // Notice a lost session on any axios traffic, which includes Inertia visits
    // (Inertia v1 shares this module singleton) and legacy glue using
    // window.axios. Only ever marks the session suspect — a probe decides
    // whether it is really gone — and always re-rejects so callers still see
    // their own errors.
    axios.interceptors.response.use(response => response, (error) => {
      const status = error?.response?.status;

      if (status === 401 || status === 419) {
        useSessionStore().markSuspect(status === 419 ? '419' : '401');
      }

      // The server has declared this tab too old to trust — mandatory reload,
      // not a dismissible suggestion.
      if (status === CONTRACT_REJECTED_STATUS
        && error?.response?.headers?.[CONTRACT_REJECTION_HEADER] !== undefined) {
        useBuildStore().requireRefresh('server-rejected');
      }

      return Promise.reject(error);
    });

    // Declare this tab's contract on everything axios sends, so the server can
    // refuse requests from a client a deploy has since made incompatible.
    // Sends nothing in the legacy design, which boots this factory but never
    // mounts Pages/App.vue and so never learns a contract — see contractHeader.
    axios.interceptors.request.use((config) => {
      Object.entries(contractHeader()).forEach(([header, value]) => {
        config.headers[header] = value;
      });

      return config;
    });

    // Deploys delete the previous content-hashed chunks, so a tab left open
    // across a deploy will 404 on its next lazy import. Register before mount so
    // a failure during initial page resolution is caught too.
    useBuildVersionCheck().registerChunkFailureListeners();

    window.addLeadFromMap = (leadData) => {
      window.dispatchEvent(new CustomEvent('lead-added-from-map', { detail: leadData }));
    };

    configureEcho({
      broadcaster: 'reverb',
      key: import.meta.env.VITE_REVERB_APP_KEY,
      wsHost: import.meta.env.VITE_REVERB_HOST,
      wsPort: import.meta.env.VITE_REVERB_PORT,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
    });

    /** @deprecated directly import date-fns instead of using moment */
    this.#app.config.globalProperties.$moment = moment;
    /** @deprecated directly import date-fns when needed */
    this.#app.config.globalProperties.$dateFns = dateFns;
    /** @deprecated import lodash directly where needed */
    this.#app.config.globalProperties.$lodash = _;

    this.#internalSetupComplete.resolve(true);
  }

  #buildTitle(initialTitle: string): string {
    if (!initialTitle) return 'Apparatix';
    if (initialTitle.toLowerCase().endsWith('apparatix')) return initialTitle;
    return `${initialTitle} - Apparatix`;
  }
}
